import { useState, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import type { IntentType, ABTest, ContentType, CustomAxis } from "@/types/ab-tests";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/contexts/AppDataContext";
import { productApi } from "@/resources/product";
import { EditTestSectionDrawer, type EditableSection } from "../EditTestSectionDrawer";
import { EditCompletedTestView } from "../EditCompletedTestView";
import { EditTestForm } from "./EditTestForm";
import { getBulletIndex } from "@/utils/ab-test-utils";

export interface HeroAbTestEditFormPayload {
  id: string;
  scopeFields: ContentType[];
  contentType: ContentType;
  targetPhrase: string;
  intentFrom: IntentType;
  intentTo: IntentType;
  hypothesis: string;
  variantATitle?: string;
  variantABullets: string[];
  variantBTitle?: string;
  variantBBullets: string[];
}

interface EditTestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  test: ABTest;
  currentTitle: string;
  bulletPoints: string[];
  productId?: number;
  brandId?: number | null;
  onSave: (updatedTest: HeroAbTestEditFormPayload) => void | Promise<void>;
  onSavePatch?: (testId: string, patch: Partial<ABTest>) => void;
  isSaving?: boolean;
}

export function EditTestModal({
  open,
  onOpenChange,
  test,
  currentTitle,
  bulletPoints,
  productId,
  brandId,
  onSave,
  onSavePatch,
  isSaving = false,
}: EditTestModalProps) {
  const { selectedBrandId } = useAppData();
  const resolvedBrandId = brandId ?? selectedBrandId;
  const parsedProductId = String(productId ?? "").replace(/^hero-/i, "");
  const { data: productKeywords } = productApi.useGetHeroProductKeywords(parsedProductId, resolvedBrandId);
  const keywordOptions = useMemo(
    () =>
      productKeywords
        ? [...new Set(productKeywords.map((kw) => kw.search))].filter((kw) => kw.length > 3).sort()
        : [],
    [productKeywords],
  );

  const [editingSection, setEditingSection] = useState<EditableSection | null>(null);
  const isCompleted = test.status === "completed" || !!test.outcome;
  const [selectedFields, setSelectedFields] = useState<ContentType[]>([]);
  const [targetPhrase, setTargetPhrase] = useState("");
  const [keywordToRemove, setKeywordToRemove] = useState("");
  const [intentFrom, setIntentFrom] = useState<IntentType>(test.intentShift.from);
  const [intentTo, setIntentTo] = useState<IntentType>(test.intentShift.to);
  const [hypothesis, setHypothesis] = useState("");
  const [variantATitle, setVariantATitle] = useState("");
  const [variantABullets, setVariantABullets] = useState<Record<string, string>>({});
  const [variantBTitle, setVariantBTitle] = useState("");
  const [variantBBullets, setVariantBBullets] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [customAxes, setCustomAxes] = useState<CustomAxis[]>(test.customAxes ?? []);

  const showIntent = test.testType === "intent" || test.testType === "intent-keyword";
  const showKeyword = test.testType === "keyword" || test.testType === "intent-keyword";
  const showCustom = test.testType === "custom";

  useEffect(() => {
    if (open) {
      const fields = test.scopeFields || [test.contentType];
      setSelectedFields(fields);
      setTargetPhrase(test.targetKeyword || test.targetCluster || "");
      setKeywordToRemove(test.keywordToRemove || "");
      setIntentFrom(test.intentShift.from);
      setIntentTo(test.intentShift.to);
      setHypothesis(test.hypothesis);
      setCustomAxes(test.customAxes ?? []);
      setVariantATitle(test.variantA?.title ?? currentTitle);
      const aBullets: Record<string, string> = {};
      const aSource = test.variantA?.bullets ?? bulletPoints;
      fields.filter((f) => f.startsWith("bullet-")).forEach((f, idx) => {
        if (aSource[idx] !== undefined) aBullets[f] = aSource[idx];
      });
      setVariantABullets(aBullets);
      setVariantBTitle(test.variantB.title ?? "");
      const bBullets: Record<string, string> = {};
      if (test.variantB.bullets) {
        fields.filter((f) => f.startsWith("bullet-")).forEach((f, idx) => {
          if (test.variantB.bullets && test.variantB.bullets[idx]) {
            bBullets[f] = test.variantB.bullets[idx];
          }
        });
      }
      setVariantBBullets(bBullets);
    }
  }, [open, test, currentTitle, bulletPoints]);

  const toggleField = (field: ContentType) => {
    if (!selectedFields.includes(field)) {
      const idx = getBulletIndex(field);
      const current = idx === null ? undefined : bulletPoints[idx];
      if (current !== undefined) {
        setVariantABullets((prev) => (prev[field] === undefined ? { ...prev, [field]: current } : prev));
      }
    }
    setSelectedFields((prev) => prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]);
  };

  const toggleCustomAxis = (axis: CustomAxis) =>
    setCustomAxes((prev) => prev.includes(axis) ? prev.filter((a) => a !== axis) : [...prev, axis]);

  const generateCopyMutation = productApi.useGenerateHeroAbTestCopy();

  const handleGenerate = async () => {
    if (showIntent && !intentTo) {
      toast({ title: "Select Intent", description: "Please select a target intent before generating.", variant: "destructive" });
      return;
    }
    if (!resolvedBrandId) {
      toast({ title: "Brand required", description: "Select a brand first.", variant: "destructive" });
      return;
    }
    const numericProductId = Number(parsedProductId);
    if (!Number.isFinite(numericProductId) || numericProductId <= 0) return;
    setIsGenerating(true);
    try {
      const generated = await generateCopyMutation.mutateAsync({
        productId: numericProductId,
        brandId: resolvedBrandId,
        intentFrom: showIntent ? intentFrom : undefined,
        intentTo: showIntent ? intentTo : undefined,
        targetKeyword: showKeyword ? targetPhrase : undefined,
        hypothesis: hypothesis.trim() || undefined,
        selectedFields,
        variantATitle,
        variantABullets,
      });
      if (selectedFields.includes("title") && generated.title) setVariantBTitle(generated.title);
      const newBullets = { ...variantBBullets };
      for (const f of selectedFields) {
        if (f.startsWith("bullet-") && generated.bulletsByField?.[f]) {
          newBullets[f] = String(generated.bulletsByField[f]).trim();
        }
      }
      setVariantBBullets(newBullets);
      toast({ title: "Content Generated", description: "Review and edit the generated content as needed." });
    } catch {
      toast({ title: "Generation failed", description: "Could not generate copy. Try again.", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedFields.length === 0) {
      toast({ title: "Select Fields", description: "Please select at least one listing field.", variant: "destructive" });
      return;
    }
    if (showKeyword && !targetPhrase) {
      toast({ title: "Missing Keyword", description: "Please enter a target keyword.", variant: "destructive" });
      return;
    }
    if (showIntent && !intentTo) {
      toast({ title: "Missing Intent", description: "Please select a target intent.", variant: "destructive" });
      return;
    }
    if (!hypothesis) {
      toast({ title: "Missing Hypothesis", description: "Please fill in the hypothesis.", variant: "destructive" });
      return;
    }
    const primaryField = selectedFields[0];
    const bulletFieldsInOrder = selectedFields.filter((f) => f.startsWith("bullet-"));
    if (selectedFields.includes("title") && !String(variantBTitle || "").trim()) {
      toast({ title: "Variant B title", description: "Enter the title for variant B.", variant: "destructive" });
      return;
    }
    for (const f of bulletFieldsInOrder) {
      if (!String(variantBBullets[f] || "").trim()) {
        toast({ title: "Variant B bullets", description: `Fill in ${f.replace("bullet-", "Bullet ")}.`, variant: "destructive" });
        return;
      }
    }
    const variantABulletsOrdered = bulletFieldsInOrder.map((f) => String(variantABullets[f] || "").trim());
    const variantBBulletsOrdered = bulletFieldsInOrder.map((f) => String(variantBBullets[f] || "").trim());
    try {
      await onSave({
        id: test.id,
        scopeFields: selectedFields,
        contentType: primaryField,
        targetPhrase,
        intentFrom,
        intentTo,
        hypothesis,
        variantATitle: selectedFields.includes("title") ? String(variantATitle || "").trim() : undefined,
        variantABullets: variantABulletsOrdered,
        variantBTitle: selectedFields.includes("title") ? String(variantBTitle || "").trim() : undefined,
        variantBBullets: variantBBulletsOrdered,
      });
      onOpenChange(false);
    } catch (_err) {
      void _err;
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] bg-card p-0 flex flex-col gap-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border space-y-1">
            <SheetTitle>{isCompleted ? "Edit Results & Action" : "Edit Test"}</SheetTitle>
            <SheetDescription>
              {isCompleted
                ? "The test configuration is locked. You can update the results and action."
                : "Modify the configuration for this test."}
            </SheetDescription>
          </SheetHeader>

          {isCompleted ? (
            <EditCompletedTestView
              test={test}
              productId={productId ?? null}
              brandId={resolvedBrandId}
              onReuploadResults={() => setEditingSection("results")}
              onClose={() => onOpenChange(false)}
            />
          ) : (
            <EditTestForm
              test={test}
              bulletPoints={bulletPoints}
              productId={productId}
              resolvedBrandId={resolvedBrandId}
              selectedFields={selectedFields}
              onToggleField={toggleField}
              showIntent={showIntent}
              showKeyword={showKeyword}
              showCustom={showCustom}
              intentFrom={intentFrom}
              intentTo={intentTo}
              onIntentFromChange={setIntentFrom}
              onIntentToChange={setIntentTo}
              keywordOptions={keywordOptions}
              keywordToRemove={keywordToRemove}
              targetPhrase={targetPhrase}
              onKeywordToRemoveChange={setKeywordToRemove}
              onTargetPhraseChange={setTargetPhrase}
              customAxes={customAxes}
              onToggleCustomAxis={toggleCustomAxis}
              hypothesis={hypothesis}
              onHypothesisChange={setHypothesis}
              variantATitle={variantATitle}
              variantABullets={variantABullets}
              variantBTitle={variantBTitle}
              variantBBullets={variantBBullets}
              onVariantBTitleChange={setVariantBTitle}
              onVariantBBulletChange={(field, value) => setVariantBBullets((prev) => ({ ...prev, [field]: value }))}
              isGenerating={isGenerating}
              onGenerate={() => void handleGenerate()}
              onCancel={() => onOpenChange(false)}
              onSubmit={() => void handleSubmit()}
              isSaving={isSaving}
            />
          )}
        </SheetContent>
      </Sheet>

      <EditTestSectionDrawer
        open={editingSection !== null}
        onOpenChange={(v) => { if (!v) setEditingSection(null); }}
        test={test}
        section={editingSection}
        onSave={(patch) => onSavePatch?.(test.id, patch)}
      />
    </>
  );
}
